import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  OnDestroy,
  inject,
  AfterContentInit,
  AfterContentChecked,
  CUSTOM_ELEMENTS_SCHEMA,
} from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { DocumentsService } from 'app/shared/services/documents/documents.service';
import { UserSessionsService } from 'app/shared/services/user-sessions/user-sessions.service';
import { MessagesService } from 'app/shared/services/messages/messages.service';
import { Subject, takeUntil } from 'rxjs';
import { IMessages } from 'app/interfaces';
import { NgClass, DatePipe } from '@angular/common';
import { OrchidRouterModule } from '@upmc-healthplan-npm/ods-ng';

@Component({
  selector: 'app-set-info',
  templateUrl: './set-additional-info.component.html',
  styleUrls: ['./set-additional-info.component.scss'],
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [NgClass, OrchidRouterModule, RouterLink, DatePipe],
})
export class SetAdditionalInfoComponent implements OnInit, OnDestroy, AfterContentInit, AfterContentChecked {
  questionnaireInstanceId: number | null = null;
  uploadProgres: number = 0;
  requestedInfo: string = '';
  messagesStream: IMessages[] = [];
  file: File | null = null;
  isError: boolean = false;
  uploadedDocId: string | null = null;
  documentAttached: boolean = false;
  private ngDestroy$ = new Subject<void>();
  @ViewChild('scrollContainer', { static: true }) _scrollContainer: ElementRef | null = null;
  @ViewChild('uploadBtn', { static: true }) uploadBtn: ElementRef | null = null;
  private documentsService: DocumentsService = inject(DocumentsService);
  private userService: UserSessionsService = inject(UserSessionsService);
  private messagesService: MessagesService = inject(MessagesService);
  private route: ActivatedRoute = inject(ActivatedRoute);

  ngOnInit() {
    this.documentAttached = false;
    this.isError = false;
    this.route.params.pipe(takeUntil(this.ngDestroy$)).subscribe({
      next: (params: any) => {
        if (params.hasOwnProperty('id')) {
          this.questionnaireInstanceId = params.id;
          this.getMessagesStream();
        }
      },
    });
  }

  getMessagesStream() {
    this.messagesService
      .getMessagesStream(this.questionnaireInstanceId)
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe({
        next: (res) => {
          this.messagesStream = res.messages;
          setTimeout(this.scrollThread.bind(this), 0);
        },
        error: (error) => {
          if (error['offlineMode']) {
            setTimeout(this.scrollThread.bind(this), 0);
          }
        },
      });
  }
  ngAfterContentInit(): void {
    if (this._scrollContainer) {
      this._scrollContainer.nativeElement.style.height =
        document.getElementsByTagName('body')[0].getBoundingClientRect().height - 460 + 'px';
    }
  }
  ngAfterContentChecked() {
    this.scrollThread();
  }

  scrollThread() {
    if (this._scrollContainer) {
      let el = this._scrollContainer.nativeElement;
      let diff = el.scrollHeight - el.offsetHeight;
      if (diff > 0) {
        el.scrollTop = el.scrollHeight;
      }
    }
  }

  uploadFile(event: Event) {
    const input = event.target as HTMLInputElement;
    this.isError = false;
    if (!input.files?.length) {
      return;
    }
    const file = input.files[0];
    if (
      file &&
      file.size &&
      file.size / 1024 / 1024 < 10 &&
      file.type &&
      file.type.match(/image\/jpeg|image\/jpg|image\/png|application\/pdf/)
    ) {
      this.documentsService.uploadFile(file, this.questionnaireInstanceId, true);
      this.documentsService.progress.pipe(takeUntil(this.ngDestroy$)).subscribe({
        next: (resp) => {
          this.uploadProgres = resp && resp.uploadProgress ? resp.uploadProgress : 0;
          if (resp && resp.uploadedDocument) {
            this.file = file;
            this.uploadedDocId = resp.uploadedDocument;
            this.requestedInfo = 'Uploaded ' + file.name;
            this.documentAttached = true;
            this.sendAdditionalInfo();
          }
        },
        error: (err) => {},
      });
    } else {
      if (this.uploadBtn) {
        this.uploadBtn.nativeElement.value = '';
      }
      this.isError = true;
    }
  }

  public onChangeRequestedInfo(e: Event): void {
    this.requestedInfo = (e.target as HTMLTextAreaElement).value;
  }

  sendAdditionalInfo() {
    if (this.requestedInfo.trim() !== '') {
      let data: IMessages = {
        text: this.requestedInfo.trim(),
        userFullname: this.userService.firstName ? this.userService.firstName : '',
        isAdmin: false,
      };
      if (this.uploadedDocId) {
        data['cloudDocumentUri'] = this.uploadedDocId;
      }

      this.messagesService
        .sendAdditionalInfoRequest(this.questionnaireInstanceId, data)
        .pipe(takeUntil(this.ngDestroy$))
        .subscribe({
          next: (res) => {
            this.getMessagesStream();
            this.requestedInfo = '';
            this.uploadedDocId = null;
            this.file = null;
            if (this.uploadBtn) {
              this.uploadBtn.nativeElement.value = '';
            }
          },
          error: (err) => {
            if (this.uploadBtn) {
              this.uploadBtn.nativeElement.value = '';
            }
          },
        });
    } else {
      this.requestedInfo = '';
    }
  }

  openDocument(message: any) {
    this.documentsService.downloadDocument(message.documentId, message.cloudDocumentUri, this.questionnaireInstanceId);
  }

  ngOnDestroy() {
    this.ngDestroy$.next();
    this.ngDestroy$.complete();
  }
}
